import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import ScrollableMobileCenteredBox from 'components/basic/panels/ScrollableMobileCenteredBox';
import HeaderMobile from 'components/layout/header/HeaderMobile';
import NotificationNoticeWrap from 'components/layout/notice/NotificationNoticeWrap';
import { useIsMobile } from 'hooks/useIsMobile';

import ModalGlobalPortal from './ModalGlobalPortal';
import SimpleModal, { SimpleModalProps } from './SimpleModal';

const mobileWrapCss = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  width: 100%;
  background: var(--elevation-light);
  /* Above everything - even banners on mobile (bug which hide modal header) */
  z-index: 109999;
`;

const MobileWrap = styled.div`
  ${mobileWrapCss}
`;

const MobileFormWrap = styled.form`
  ${mobileWrapCss}
`;

const MobileWrapWithOptionalFormHandler = ({
  formOnSubmit,
  children,
}: {
  children: ReactNode;
  formOnSubmit?: () => void;
}) => {
  return formOnSubmit ? (
    <MobileFormWrap
      onSubmit={event => {
        event.preventDefault();
        formOnSubmit?.();
      }}
    >
      {children}
    </MobileFormWrap>
  ) : (
    <MobileWrap>{children}</MobileWrap>
  );
};

/**
 * Automatically decide to use:
 *  Desktop - SimpleModal
 *  Mobile  - Page with mobile header
 * 
 * @example Desktop & mobile (full-screen) modal usage:
 *     ```jsx
        <SimpleModalOrMobilePage>
              <ScrollableMobileCenteredBox>
                <div>
                  <ModalContent>...</ModalContent>
                  <ModalFooterButtons confirmButtonProps={...} />
                </div>
              </ScrollableMobileCenteredBox>
              <OptionalMobileBottomFooter />
        </SimpleModalOrMobilePage>
      ```
 */
export default function SimpleModalOrMobilePage(
  params: SimpleModalProps & {
    verticalAlignOnMobile?: 'centered' | 'ceneteredDiv';
    /** Experimental option */
    noMobileHeader?: boolean;
  }
) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <ModalGlobalPortal>
      <MobileWrapWithOptionalFormHandler formOnSubmit={params.formOnSubmit}>
        {/* We have to duplicate panel here cause modal takes 100% screen */}
        <NotificationNoticeWrap />
        {!params.noMobileHeader && (
          <HeaderMobile
            onBackClick={params.onBack}
            onCloseClick={params.onClose}
          >
            {params.title}
          </HeaderMobile>
        )}
        {params.verticalAlignOnMobile ? (
          <ScrollableMobileCenteredBox mobilePadding={params.padding}>
            {params.verticalAlignOnMobile === 'ceneteredDiv' ? (
              // If children has multiple elements, we have to wrap it in block
              <div>{params.children}</div>
            ) : (
              params.children
            )}
          </ScrollableMobileCenteredBox>
        ) : params.padding ? (
          <div style={{ padding: params.padding, height: '100%' }}>
            {params.children}
          </div>
        ) : (
          params.children
        )}
      </MobileWrapWithOptionalFormHandler>
    </ModalGlobalPortal>
  ) : (
    <SimpleModal {...params} />
  );
}
